import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { X, Send, MessageCircle, Minimize2, Calendar, Mail, ArrowLeft, Paperclip, Download, FileText, Check, CheckCheck, GripVertical } from 'lucide-react';
import { useDarkMode } from '../DarkModeContext';
import { useTenant } from '../config/tenantConfig';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';

// Format timestamps: today → "14:30", yesterday → "Yesterday 14:30", older → "12 Feb 14:30"
const formatChatTime = (timestamp) => {
  if (!timestamp) return '';
  const date = timestamp instanceof Date ? timestamp : new Date(timestamp);
  if (isNaN(date.getTime())) return '';
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today); yesterday.setDate(today.getDate() - 1);
  const time = date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  if (date >= today) return time;
  if (date >= yesterday) return `Yesterday ${time}`;
  return `${date.getDate()} ${date.toLocaleString('en', { month: 'short' })} ${time}`;
};

// Get date label for separator pills
const getDateLabel = (date) => {
  const d = date instanceof Date ? date : new Date(date);
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today); yesterday.setDate(today.getDate() - 1);
  if (d >= today) return 'Today';
  if (d >= yesterday) return 'Yesterday';
  return d.toLocaleDateString('en', { day: 'numeric', month: 'short', year: 'numeric' });
};

const SupportChatAgent = ({ initiallyOpen = false, onOpenChange, pageName = 'Support' }) => {
  const [isOpen, setIsOpen] = useState(initiallyOpen);
  const [messages, setMessages] = useState([
    {
      type: 'bot',
      text: "👋 Hello! I'm your HaliSoft AI-powered support assistant. \n\nWe provide:\n• Invoice Factoring & Dynamic Discounting\n• Supply Chain Financing & Payables Programs\n• White-label SaaS for Financial Institutions\n• Trade Finance Consulting for Customers & Financial Entities\n\n🤖 Everything is done 100% online through our AI-powered platform - no email submissions!\n\nHow can I help you today?",
      timestamp: new Date()
    }
  ]);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [conversationId, setConversationId] = useState(null);
  const [showCalendly, setShowCalendly] = useState(false);
  const [showLiveChat, setShowLiveChat] = useState(false);
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [emailFormData, setEmailFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [emailSending, setEmailSending] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [liveChatMessages, setLiveChatMessages] = useState([]);
  const [liveChatInput, setLiveChatInput] = useState('');
  const [chatSessionId, setChatSessionId] = useState(() => {
    // Restore session ID from localStorage if it exists
    const savedSession = localStorage.getItem('liveChatSessionId') || null;
    // If we have a non-guest session but user is not authenticated, clear it
    // This will be properly handled after useAuth hook is called
    return savedSession;
  });

  // Floating button position state (like HaliGuide)
  const [badgePosition, setBadgePosition] = useState(() => {
    const saved = localStorage.getItem('supportChatBadgePosition');
    return saved ? JSON.parse(saved) : null;
  });
  const [badgeDrag, setBadgeDrag] = useState({ isDragging: false, offsetX: 0, offsetY: 0, startX: 0, startY: 0 });
  const badgeSuppressClickRef = useRef(false);

  // Resizable and movable panel state (like HaliGuide)
  const [panelWidth, setPanelWidth] = useState(() => {
    const saved = localStorage.getItem('supportChatPanelWidth');
    return saved ? parseInt(saved, 10) : 384; // Default 384px
  });
  const [isResizing, setIsResizing] = useState(false);
  const panelRef = useRef(null);
  const [panelPosition, setPanelPosition] = useState(() => {
    const saved = localStorage.getItem('supportChatPanelPosition');
    return saved ? JSON.parse(saved) : null;
  });
  const [panelDrag, setPanelDrag] = useState({ isDragging: false, offsetX: 0, offsetY: 0 });

  // Helper to check if current session is a guest session
  const isGuestSession = (sessionId) => sessionId && sessionId.startsWith('chat_guest_');

  // Helper to get the correct API path based on session type
  const getChatApiPath = (endpoint, sessionId = chatSessionId) => {
    // Use guest routes for guest sessions OR when not authenticated
    if (isGuestSession(sessionId) || !isAuthenticated) {
      return `/api/support/chat/guest/${endpoint}`;
    }
    return `/api/support/chat/${endpoint}`;
  };
  const [selectedFile, setSelectedFile] = useState(null);
  const [isAIOnline, setIsAIOnline] = useState(true); // Track AI connection status
  const [isLiveAgentOnline, setIsLiveAgentOnline] = useState(false); // Track live agent availability
  const [unreadCount, setUnreadCount] = useState(0); // Track unread messages from support
  const [newMessageAlert, setNewMessageAlert] = useState(false); // Flash alert for new messages
  const [lastReadMessageId, setLastReadMessageId] = useState(() => {
    // Restore last read message ID from localStorage
    const saved = localStorage.getItem('liveChatLastReadMessageId');
    return saved ? parseInt(saved, 10) : 0;
  });
  const fileInputRef = useRef(null);
  const messagesEndRef = useRef(null);
  const { darkMode } = useDarkMode();
  const { theme } = useTenant();
  const { user, isAuthenticated } = useAuth();

  // Clear invalid sessions - if we have a non-guest session but user is not authenticated
  useEffect(() => {
    if (chatSessionId && !isGuestSession(chatSessionId) && !isAuthenticated) {
      console.log('[SupportChatAgent] Clearing invalid authenticated session for unauthenticated user');
      setChatSessionId(null);
      setLiveChatMessages([]);
      localStorage.removeItem('liveChatSessionId');
    }
    if (chatSessionId && isAuthenticated && user?.id && !isGuestSession(chatSessionId)) {
      const expectedPrefix = `chat_${user.id}_`;
      if (!chatSessionId.startsWith(expectedPrefix)) {
        console.log('[SupportChatAgent] Clearing session for different authenticated user');
        setChatSessionId(null);
        setLiveChatMessages([]);
        localStorage.removeItem('liveChatSessionId');
      }
    }
  }, [chatSessionId, isAuthenticated, user?.id]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Also scroll to bottom when live chat messages update
  useEffect(() => {
    scrollToBottom();
  }, [liveChatMessages]);

  useEffect(() => {
    setIsOpen(initiallyOpen);
  }, [initiallyOpen]);

  useEffect(() => {
    if (onOpenChange) {
      onOpenChange(isOpen);
    }
  }, [isOpen, onOpenChange]);

  useEffect(() => {
    const handleExternalOpen = () => {
      console.log('[SupportChatAgent] Received supportChat:open event');
      setIsOpen(true);
      // Reset to AI chat mode when opening
      setShowLiveChat(false);
      setShowCalendly(false);
      setShowEmailForm(false);
    };
    const handleExternalClose = () => setIsOpen(false);
    const handleOpenCalendly = () => {
      console.log('[SupportChatAgent] Received supportChat:openCalendly event');
      setIsOpen(true);
      setShowCalendly(true);
      setShowLiveChat(false);
      setShowEmailForm(false);
    };
    const handleExternalOpenLiveChat = () => {
      console.log('[SupportChatAgent] Received supportChat:openLiveChat event');
      setIsOpen(true);
      handleOpenLiveChat();
    };

    if (typeof window !== 'undefined') {
      window.addEventListener('supportChat:open', handleExternalOpen);
      window.addEventListener('supportChat:close', handleExternalClose);
      window.addEventListener('supportChat:openCalendly', handleOpenCalendly);
      window.addEventListener('supportChat:openLiveChat', handleExternalOpenLiveChat);
    }

    return () => {
      if (typeof window !== 'undefined') {
        window.removeEventListener('supportChat:open', handleExternalOpen);
        window.removeEventListener('supportChat:close', handleExternalClose);
        window.removeEventListener('supportChat:openCalendly', handleOpenCalendly);
        window.removeEventListener('supportChat:openLiveChat', handleExternalOpenLiveChat);
      }
    };
  }, []);

  const quickReplies = [
    { label: '🚀 Our Products', value: 'Tell me about your products' },
    { label: '🌍 Coverage', value: 'What regions do you cover?' },
    { label: '🤖 AI Platform', value: 'How does your AI platform work?' },
    { label: '📋 Get Started', value: 'How do I get started?' },
    { label: '💰 Pricing', value: 'How much does factoring cost?' },
    { label: '⏱️ Timeline', value: 'How long does approval take?' },
    { label: '🏦 For Banks', value: 'Tell me about white-label solution' },
    { label: '📞 Contact', value: 'I need to speak with someone' }
  ];

  const liveChatSuggestedReplies = [
    {
      match: /which payment term best describes your typical arrangement\?/i,
      options: [
        { label: 'Net 30', value: 'Net 30 (payment due 30 days after invoice).' },
        { label: 'Net 60', value: 'Net 60 (payment due 60 days after invoice).' },
        { label: 'Other', value: 'Other (please specify).' }
      ]
    }
  ];

  // Call the AI backend
  const sendMessageToAI = async (text) => {
    try {
      const response = await api.post('/api/chat/chat', {
        message: text,
        conversationId: conversationId,
        userId: 'guest' // Could be replaced with actual user ID if logged in
      });

      if (response.data.success) {
        // AI is online if we get a successful response
        setIsAIOnline(true);
        // Save conversation ID for context
        if (response.data.conversationId && !conversationId) {
          setConversationId(response.data.conversationId);
        }
        return response.data.message;
      } else {
        throw new Error('Failed to get response from AI');
      }
    } catch (error) {
      console.error('AI Chat Error:', error);
      // AI is offline if request fails
      setIsAIOnline(false);

      // Fallback error message
      if (error.response?.status === 429) {
        return "I'm experiencing high demand right now. Please try again in a moment, or contact us directly at info@halisoft.biz.";
      }

      return "I'm having trouble connecting right now. Please contact our team directly:\n\n📧 Email: info@halisoft.biz\n📞 Phone: +971 58 833 9129";
    }
  };

  const handleSendMessage = async (text = inputValue) => {
    if (!text.trim()) return;

    // Add user message
    const userMessage = {
      type: 'user',
      text: text.trim(),
      timestamp: new Date()
    };
    setMessages(prev => [...prev, userMessage]);
    setInputValue('');

    // Show typing indicator
    setIsTyping(true);

    try {
      // Get AI response
      const aiResponse = await sendMessageToAI(text.trim());

      setIsTyping(false);

      // Add bot message
      const botMessage = {
        type: 'bot',
        text: aiResponse,
        timestamp: new Date()
      };

      setMessages(prev => [...prev, botMessage]);
    } catch (error) {
      setIsTyping(false);
      console.error('Error sending message:', error);

      // Add error message
      const errorMessage = {
        type: 'bot',
        text: "I'm having trouble connecting right now. Please contact our team directly:\n\n📧 Email: info@halisoft.biz\n📞 Phone: +971 58 833 9129",
        timestamp: new Date()
      };

      setMessages(prev => [...prev, errorMessage]);
    }
  };

  const handleQuickReply = (value) => {
    // Send the quick reply text as a regular message
    handleSendMessage(value);
  };

  const getLiveChatSuggestions = () => {
    if (liveChatInput.trim()) return [];
    const lastIncoming = [...liveChatMessages].reverse().find((msg) => msg.type === 'support' || msg.type === 'system');
    if (!lastIncoming?.text) return [];
    const match = liveChatSuggestedReplies.find((entry) => entry.match.test(lastIncoming.text));
    return match ? match.options : [];
  };

  const toggleChat = () => {
    setIsOpen(!isOpen);
    // Close embedded views when closing chat
    if (isOpen) {
      setShowCalendly(false);
      setShowEmailForm(false);
    }
  };

  const handleOpenCalendly = () => {
    setShowCalendly(true);
    setShowLiveChat(false);
    setShowEmailForm(false);
  };

  const handleOpenEmailForm = () => {
    setShowEmailForm(true);
    setShowCalendly(false);
    setShowLiveChat(false);
    setEmailSent(false);
  };

  const handleEmailFormChange = (field, value) => {
    setEmailFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSendEmail = async () => {
    if (!emailFormData.name || !emailFormData.email || !emailFormData.message) {
      return;
    }

    setEmailSending(true);
    try {
      const response = await api.post('/public-api/contact/send-email', emailFormData);
      if (response.data.success) {
        setEmailSent(true);
        setEmailFormData({ name: '', email: '', subject: '', message: '' });
      }
    } catch (error) {
      console.error('Error sending email:', error);
      alert('Failed to send email. Please try again or contact us directly at info@halisoft.biz');
    } finally {
      setEmailSending(false);
    }
  };

  // Restore live chat messages when component mounts with existing session
  useEffect(() => {
    const restoreMessages = async () => {
      if (chatSessionId && liveChatMessages.length === 0) {
        try {
          console.log('[SupportChatAgent] Restoring messages for session:', chatSessionId);
          const apiPath = isGuestSession(chatSessionId)
            ? `/api/support/chat/guest/messages/${chatSessionId}`
            : `/api/support/chat/messages/${chatSessionId}`;
          const response = await api.get(apiPath);

          if (response.data.success && response.data.messages) {
            const formattedMessages = response.data.messages.map(msg => ({
              id: msg.id,
              type: msg.sender === 'support' ? 'support' : msg.sender === 'customer' ? 'user' : 'system',
              text: msg.text,
              senderName: msg.senderName,
              timestamp: new Date(msg.timestamp),
              attachments: msg.attachments || []
            }));

            setLiveChatMessages(formattedMessages);
            console.log('[SupportChatAgent] Restored', formattedMessages.length, 'messages');
          }
        } catch (error) {
          console.error('[SupportChatAgent] Error restoring messages:', error);
          // If session is invalid (403/404/500), clear it so user can start fresh
          if (error.response?.status === 403 || error.response?.status === 404 || error.response?.status === 500) {
            console.log('[SupportChatAgent] Clearing invalid chat session due to', error.response?.status);
            setChatSessionId(null);
            setLiveChatMessages([]);
            localStorage.removeItem('liveChatSessionId');
            localStorage.removeItem('liveChatLastReadMessageId');
            setUnreadCount(0);
          }
        }
      }
    };

    restoreMessages();
  }, [chatSessionId]);

  // Poll for new live chat messages
  useEffect(() => {
    if (!chatSessionId || !showLiveChat) return;

    const pollMessages = async () => {
      try {
        const apiPath = isGuestSession(chatSessionId)
          ? `/api/support/chat/guest/messages/${chatSessionId}`
          : `/api/support/chat/messages/${chatSessionId}`;
        const response = await api.get(apiPath);

        if (response.data.success && response.data.messages) {
          console.log('[SupportChatAgent] Polled messages:', {
            sessionId: chatSessionId,
            messageCount: response.data.messages.length,
            messages: response.data.messages.map(m => ({
              id: m.id,
              sender: m.sender,
              text: m.text?.substring(0, 30),
              attachmentCount: m.attachments?.length || 0,
              attachments: m.attachments
            }))
          });

          // Convert backend message format to frontend format
          const formattedMessages = response.data.messages.map(msg => ({
            id: msg.id,
            type: msg.sender === 'support' ? 'support' : msg.sender === 'customer' ? 'user' : 'system',
            text: msg.text,
            senderName: msg.senderName,
            timestamp: new Date(msg.timestamp),
            attachments: msg.attachments || []
          }));

          const pendingLocalMessages = liveChatMessages.filter(
            (msg) => String(msg.id || '').startsWith('temp_') || msg.status === 'sending' || msg.status === 'failed'
          );
          const serverMessageIds = new Set(formattedMessages.map((msg) => msg.id));
          const mergedMessages = [
            ...formattedMessages,
            ...pendingLocalMessages.filter((msg) => !serverMessageIds.has(msg.id))
          ];

          // Only update if message count changed or new message IDs
          const currentIds = liveChatMessages.map(m => m.id).join(',');
          const newIds = mergedMessages.map(m => m.id).join(',');

          if (currentIds !== newIds || formattedMessages.length !== liveChatMessages.length) {
            // Check if there's a new support message
            const hasNewSupportMessage = formattedMessages.some(
              msg => msg.type === 'support' && msg.id > lastReadMessageId
            );

            if (hasNewSupportMessage) {
              // Flash the new message alert
              setNewMessageAlert(true);
              setTimeout(() => setNewMessageAlert(false), 3000);

              // Play notification sound (optional)
              try {
                const audio = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdH2FgX1ycnl+goKCfHl0b3Z+gX93dHFwd3x+fXt2dHJzdnh6enp3dXR1d3l6enh2dXV2eHl5eHd2dnd4eXl4d3Z2d3h5eXh3dnZ3eHl5eHd2dnd4eXl4d3Z2d3h5eQ==');
                audio.volume = 0.3;
                audio.play().catch(() => {});
              } catch (e) {}
            }

            setLiveChatMessages(mergedMessages);

            // Force scroll to bottom after state update
            setTimeout(() => {
              messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
            }, 100);
          }

          // Mark all messages as read when viewing live chat
          const maxMessageId = Math.max(...response.data.messages.map(m => m.id || 0));
          if (maxMessageId > lastReadMessageId) {
            setLastReadMessageId(maxMessageId);
            localStorage.setItem('liveChatLastReadMessageId', maxMessageId.toString());
            setUnreadCount(0);
          }
        }
      } catch (error) {
        console.error('Error polling messages:', error);
        // If session is invalid (403/404/500), clear it so user can start fresh
        if (error.response?.status === 403 || error.response?.status === 404 || error.response?.status === 500) {
          console.log('[SupportChatAgent] Clearing invalid chat session due to', error.response?.status);
          setChatSessionId(null);
          setLiveChatMessages([]);
          localStorage.removeItem('liveChatSessionId');
          localStorage.removeItem('liveChatLastReadMessageId');
          setUnreadCount(0);
        }
      }
    };

    // Poll every 5 seconds (reduced from 2s to lower API load)
    const interval = setInterval(pollMessages, 5000);

    // Initial fetch
    pollMessages();

    return () => clearInterval(interval);
  }, [chatSessionId, showLiveChat, lastReadMessageId]);

  // Poll for unread messages count (even when not in live chat view)
  useEffect(() => {
    if (!chatSessionId || showLiveChat) return; // Don't poll if in live chat (handled above)

    const pollUnreadCount = async () => {
      try {
        const apiPath = isGuestSession(chatSessionId)
          ? `/api/support/chat/guest/messages/${chatSessionId}`
          : `/api/support/chat/messages/${chatSessionId}`;
        const response = await api.get(apiPath);

        if (response.data.success && response.data.messages) {
          // Count support messages that are newer than lastReadMessageId
          const newSupportMessages = response.data.messages.filter(
            msg => msg.sender === 'support' && msg.id > lastReadMessageId
          );
          setUnreadCount(newSupportMessages.length);
        }
      } catch (error) {
        console.error('Error polling unread count:', error);
        // If session is invalid (403/404/500), clear it so user can start fresh
        if (error.response?.status === 403 || error.response?.status === 404 || error.response?.status === 500) {
          console.log('[SupportChatAgent] Clearing invalid chat session due to', error.response?.status);
          setChatSessionId(null);
          setLiveChatMessages([]);
          localStorage.removeItem('liveChatSessionId');
          localStorage.removeItem('liveChatLastReadMessageId');
          setUnreadCount(0);
        }
      }
    };

    // Poll every 15 seconds (reduced from 5s to lower API load)
    const interval = setInterval(pollUnreadCount, 15000);

    // Initial fetch
    pollUnreadCount();

    return () => clearInterval(interval);
  }, [chatSessionId, showLiveChat, lastReadMessageId]);

  const handleOpenLiveChat = async () => {
    setShowLiveChat(true);
    setShowCalendly(false);

    // Get expected user ID for this session
    const expectedUserId = isAuthenticated && user?.id ? String(user.id) : 'guest';

    // Check if existing session matches the current user
    // Session format is: chat_{userId}_{timestamp} for authenticated, chat_guest_{timestamp} for guests
    const sessionMatchesUser = chatSessionId && (
      (isAuthenticated && chatSessionId.startsWith(`chat_${expectedUserId}_`)) ||
      (!isAuthenticated && chatSessionId.startsWith('chat_guest_'))
    );

    // Initialize live chat session if none exists OR if existing session doesn't match current user
    if (!chatSessionId || !sessionMatchesUser) {
      if (chatSessionId && !sessionMatchesUser) {
        console.log('[SupportChatAgent] Existing session does not match current user, creating new session...', {
          existingSession: chatSessionId,
          expectedUserId,
          isAuthenticated
        });
      }

      try {
        const userName = isAuthenticated && user ?
          `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.email || 'Guest User'
          : 'Guest Visitor';

        let response;
        if (isAuthenticated) {
          // Use authenticated endpoint
          console.log('[SupportChatAgent] Starting authenticated live chat session...', { userId: expectedUserId, userName });
          response = await api.post('/api/support/chat/start', {
            userId: expectedUserId,
            userName
          });
        } else {
          // Use guest endpoint (no auth required)
          console.log('[SupportChatAgent] Starting guest live chat session...', { userName });
          response = await api.post('/api/support/chat/guest/start', {
            userName
          });
        }

        console.log('[SupportChatAgent] Live chat session response:', response.data);

        if (response.data.success) {
          const sessionId = response.data.sessionId;
          setChatSessionId(sessionId);
          localStorage.setItem('liveChatSessionId', sessionId); // Persist session ID
          console.log('[SupportChatAgent] Live chat session created:', sessionId);

          setLiveChatMessages([{
            type: 'system',
            text: 'Connected to HaliSoft support team. A team member will be with you shortly.',
            timestamp: new Date()
          }]);
        }
      } catch (error) {
        console.error('[SupportChatAgent] Error starting live chat:', error);
        console.error('[SupportChatAgent] Error details:', error.response?.data);
        setLiveChatMessages([{
          type: 'system',
          text: 'Unable to connect to support team. Please try again or use alternative contact methods.',
          timestamp: new Date()
        }]);
      }
    } else {
      console.log('[SupportChatAgent] Using existing session:', chatSessionId);
    }
  };

  const handleSendLiveChatMessage = async (overrideText) => {
    const trimmedText = (overrideText ?? liveChatInput).trim();
    const shouldSendFile = !overrideText && selectedFile;
    if ((!trimmedText && !shouldSendFile) || !chatSessionId) return;

    const messageText = trimmedText || (shouldSendFile ? `[Attachment: ${selectedFile.name}]` : '');
    const tempId = `temp_${Date.now()}`;
    const userMessage = {
      id: tempId,
      type: 'user',
      text: messageText,
      timestamp: new Date(),
      status: 'sending', // Track message status: sending, sent, delivered, read
      attachments: shouldSendFile ? [{
        fileName: selectedFile.name,
        fileSize: selectedFile.size,
        mimeType: selectedFile.type,
        uploading: true
      }] : []
    };

    setLiveChatMessages(prev => [...prev, userMessage]);
    const messageInput = trimmedText;
    const fileToSend = shouldSendFile ? selectedFile : null;
    setLiveChatInput('');
    if (fileToSend) {
      setSelectedFile(null);
    }

    try {
      const useGuestApi = isGuestSession(chatSessionId);

      // If there's a file attachment, use the upload-attachment endpoint
      // Note: Guest users don't have file upload support yet, only text messages
      if (fileToSend && !useGuestApi) {
        console.log('[SupportChatAgent] Sending message with attachment:', fileToSend.name);
        const formData = new FormData();
        formData.append('sessionId', chatSessionId);
        formData.append('text', messageInput);
        formData.append('sender', 'customer');
        formData.append('attachment', fileToSend);

        const response = await api.post('/api/support/chat/upload-attachment', formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });
        console.log('[SupportChatAgent] Message with attachment sent successfully:', response.data);

        // Update message status to sent
        setLiveChatMessages(prev => prev.map(msg =>
          msg.id === tempId ? { ...msg, status: 'sent' } : msg
        ));
      } else {
        // Regular text message - use guest or authenticated endpoint
        console.log('[SupportChatAgent] Sending message:', messageText, 'useGuestApi:', useGuestApi);
        const apiPath = useGuestApi
          ? '/api/support/chat/guest/message'
          : '/api/support/chat/message';
        const response = await api.post(apiPath, {
          sessionId: chatSessionId,
          text: messageText
        });
        console.log('[SupportChatAgent] Message sent successfully:', response.data);

        // Update message status to sent
        setLiveChatMessages(prev => prev.map(msg =>
          msg.id === tempId ? { ...msg, status: 'sent' } : msg
        ));
      }
    } catch (error) {
      console.error('[SupportChatAgent] Error sending message:', error);
      console.error('[SupportChatAgent] Error details:', error.response?.data);

      // Update message status to failed
      setLiveChatMessages(prev => prev.map(msg =>
        msg.id === tempId ? { ...msg, status: 'failed' } : msg
      ));
    }
  };

  const handleRetryLiveChatMessage = async (message) => {
    if (!chatSessionId || !message?.text) return;

    setLiveChatMessages(prev => prev.map(msg =>
      msg.id === message.id ? { ...msg, status: 'sending' } : msg
    ));

    try {
      const useGuestApi = isGuestSession(chatSessionId);
      const apiPath = useGuestApi
        ? '/api/support/chat/guest/message'
        : '/api/support/chat/message';
      const response = await api.post(apiPath, {
        sessionId: chatSessionId,
        text: message.text
      });
      console.log('[SupportChatAgent] Message retry sent successfully:', response.data);

      setLiveChatMessages(prev => prev.map(msg =>
        msg.id === message.id ? { ...msg, status: 'sent' } : msg
      ));
    } catch (error) {
      console.error('[SupportChatAgent] Error retrying message:', error);
      console.error('[SupportChatAgent] Retry error details:', error.response?.data);
      setLiveChatMessages(prev => prev.map(msg =>
        msg.id === message.id ? { ...msg, status: 'failed' } : msg
      ));
    }
  };

  const handleFileSelect = (event) => {
    const file = event.target.files?.[0];
    if (file) {
      // Check file size (10MB limit)
      if (file.size > 10 * 1024 * 1024) {
        alert('File size must be less than 10MB');
        return;
      }
      setSelectedFile(file);
    }
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleDownloadAttachment = async (attachmentId, fileName) => {
    try {
      console.log('[SupportChatAgent] Downloading attachment:', { attachmentId, fileName });
      const response = await api.get(`/api/support/chat/download/${attachmentId}`, {
        responseType: 'blob'
      });

      console.log('[SupportChatAgent] Attachment downloaded successfully:', {
        attachmentId,
        fileName,
        size: response.data.size
      });

      // Create a download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('[SupportChatAgent] Error downloading attachment:', error);
      alert('Failed to download attachment');
    }
  };

  const handleCloseEmbeddedView = () => {
    setShowCalendly(false);
    setShowLiveChat(false);
    setShowEmailForm(false);
  };

  const handleBackToAI = () => {
    setShowLiveChat(false);
    // Keep the live chat session active in case user wants to return
  };

  const liveChatSuggestions = getLiveChatSuggestions();

  const chatContent = (
    <>
      {/* Chat Button - Hidden: Access via Platform Navigator only */}
      {false && !isOpen && (
        <button
          onClick={toggleChat}
          className="flex fixed bottom-[7.5rem] left-4 md:bottom-6 md:right-6 md:left-auto shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 text-white items-center gap-3 px-5 py-3 rounded-full"
          style={{
            backgroundColor: theme.primary,
            zIndex: 9999,
            pointerEvents: 'auto'
          }}
          aria-label="Open support chat"
        >
          <MessageCircle size={24} />
          <span className="font-semibold text-base">Contact Center 💬</span>
        </button>
      )}

      {/* Chat Window */}
      {isOpen && (
        <div
          className={`fixed bottom-0 right-0 left-0 md:bottom-6 md:right-6 md:left-auto w-full md:w-96 h-[100vh] md:h-[calc(100vh-100px)] md:max-h-[800px] ${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-t-2xl md:rounded-2xl shadow-2xl flex flex-col overflow-hidden border ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}
          style={{ zIndex: 9999, pointerEvents: 'auto' }}
        >
          {/* Header */}
          <div
            className="p-4 text-white flex items-center justify-between"
            style={{ backgroundColor: theme.primary }}
          >
            <div className="flex items-center gap-3">
              <MessageCircle size={24} />
              <div>
                <h3 className="font-semibold">Contact Center - {pageName}</h3>
                <div className="flex items-center gap-3 text-xs opacity-90">
                  <div className="flex items-center gap-1.5">
                    <div className={`w-2 h-2 rounded-full ${isAIOnline ? 'bg-green-400 animate-pulse' : 'bg-red-400'}`}></div>
                    <span>{isAIOnline ? 'AI Online' : 'AI Offline'}</span>
                  </div>
                  {!showLiveChat && (
                    <div className="flex items-center gap-1.5">
                      <div className={`w-2 h-2 rounded-full ${isLiveAgentOnline ? 'bg-green-400 animate-pulse' : 'bg-gray-400'}`}></div>
                      <span>{isLiveAgentOnline ? 'Live' : 'Live (offline)'}</span>
                      {unreadCount > 0 && (
                        <span className="bg-red-500 text-white text-xs font-bold rounded-full px-1.5 py-0.5 ml-1 animate-pulse">
                          {unreadCount} new
                        </span>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="flex gap-2">
              <button
                onClick={toggleChat}
                className="p-1 hover:bg-white/20 rounded transition-colors"
                aria-label="Minimize chat"
              >
                <Minimize2 size={20} />
              </button>
              <button
                onClick={toggleChat}
                className="p-1 hover:bg-white/20 rounded transition-colors"
                aria-label="Close chat"
              >
                <X size={20} />
              </button>
            </div>
          </div>

          {/* Messages or Embedded View */}
          <div className={`flex-1 overflow-y-auto ${darkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
            {showCalendly ? (
              // Calendly Embedded View
              <div className="h-full flex flex-col">
                <div className={`flex items-center justify-between p-3 border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                  <h3 className={`font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>Schedule a Call</h3>
                  <button
                    onClick={handleCloseEmbeddedView}
                    className={`p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}
                  >
                    <X size={20} />
                  </button>
                </div>
                <div className="flex-1">
                  <iframe
                    src="https://calendly.com/loiclamy/30min"
                    width="100%"
                    height="100%"
                    frameBorder="0"
                    title="Schedule Meeting"
                  ></iframe>
                </div>
              </div>
            ) : showEmailForm ? (
              // Email Contact Form
              <div className="h-full flex flex-col">
                <div className={`flex items-center justify-between p-3 border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                  <h3 className={`font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>Send us an Email</h3>
                  <button
                    onClick={handleCloseEmbeddedView}
                    className={`p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}
                  >
                    <X size={20} />
                  </button>
                </div>
                <div className="flex-1 overflow-y-auto p-4">
                  {emailSent ? (
                    <div className="flex flex-col items-center justify-center h-full text-center">
                      <div className="w-16 h-16 rounded-full bg-green-100 dark:bg-green-900 flex items-center justify-center mb-4">
                        <Mail size={32} className="text-green-600 dark:text-green-400" />
                      </div>
                      <h3 className={`text-lg font-semibold mb-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                        Email Sent Successfully!
                      </h3>
                      <p className={`text-sm mb-4 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                        We'll get back to you within 24 hours.
                      </p>
                      <button
                        onClick={() => { setEmailSent(false); setShowEmailForm(false); }}
                        className="px-4 py-2 rounded-lg text-white transition-colors"
                        style={{ backgroundColor: theme.primary }}
                      >
                        Back to Chat
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <div>
                        <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                          Name <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          value={emailFormData.name}
                          onChange={(e) => handleEmailFormChange('name', e.target.value)}
                          className={`w-full px-3 py-2 rounded-lg border ${
                            darkMode
                              ? 'bg-gray-800 border-gray-700 text-white placeholder-gray-500'
                              : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'
                          } focus:outline-none focus:ring-2 focus:ring-opacity-50`}
                          style={{ focusRingColor: theme.primary }}
                          placeholder="Your name"
                        />
                      </div>
                      <div>
                        <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                          Email <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="email"
                          value={emailFormData.email}
                          onChange={(e) => handleEmailFormChange('email', e.target.value)}
                          className={`w-full px-3 py-2 rounded-lg border ${
                            darkMode
                              ? 'bg-gray-800 border-gray-700 text-white placeholder-gray-500'
                              : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'
                          } focus:outline-none focus:ring-2 focus:ring-opacity-50`}
                          style={{ focusRingColor: theme.primary }}
                          placeholder="your@email.com"
                        />
                      </div>
                      <div>
                        <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                          Subject
                        </label>
                        <input
                          type="text"
                          value={emailFormData.subject}
                          onChange={(e) => handleEmailFormChange('subject', e.target.value)}
                          className={`w-full px-3 py-2 rounded-lg border ${
                            darkMode
                              ? 'bg-gray-800 border-gray-700 text-white placeholder-gray-500'
                              : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'
                          } focus:outline-none focus:ring-2 focus:ring-opacity-50`}
                          style={{ focusRingColor: theme.primary }}
                          placeholder="What is this about?"
                        />
                      </div>
                      <div>
                        <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                          Message <span className="text-red-500">*</span>
                        </label>
                        <textarea
                          value={emailFormData.message}
                          onChange={(e) => handleEmailFormChange('message', e.target.value)}
                          rows={6}
                          className={`w-full px-3 py-2 rounded-lg border ${
                            darkMode
                              ? 'bg-gray-800 border-gray-700 text-white placeholder-gray-500'
                              : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'
                          } focus:outline-none focus:ring-2 focus:ring-opacity-50 resize-none`}
                          style={{ focusRingColor: theme.primary }}
                          placeholder="How can we help you?"
                        />
                      </div>
                      <button
                        onClick={handleSendEmail}
                        disabled={emailSending || !emailFormData.name || !emailFormData.email || !emailFormData.message}
                        className={`w-full py-2.5 rounded-lg text-white font-medium transition-colors ${
                          emailSending || !emailFormData.name || !emailFormData.email || !emailFormData.message
                            ? 'opacity-50 cursor-not-allowed'
                            : 'hover:opacity-90'
                        }`}
                        style={{ backgroundColor: theme.primary }}
                      >
                        {emailSending ? 'Sending...' : 'Send Email'}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ) : showLiveChat ? (
              // Live Chat with Platform Team
              <div className="h-full flex flex-col">
                <div className={`flex items-center justify-between p-3 border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'} ${newMessageAlert ? 'bg-green-500/20 animate-pulse' : ''}`}>
                  <div className="flex items-center gap-2">
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className={`font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>Chat with Support Team</h3>
                        {newMessageAlert && (
                          <span className="bg-green-500 text-white text-xs px-2 py-0.5 rounded-full animate-bounce">
                            New message!
                          </span>
                        )}
                      </div>
                      <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                        {chatSessionId ? `Session: ${chatSessionId.slice(-8)}` : 'Connecting...'}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={handleBackToAI}
                      className={`px-2 py-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors ${darkMode ? 'text-gray-400' : 'text-gray-600'} text-xs flex items-center gap-1`}
                      title="Back to AI Chat"
                    >
                      <ArrowLeft size={14} />
                      Back
                    </button>
                    <button
                      onClick={() => {
                        localStorage.removeItem('liveChatSessionId');
                        localStorage.removeItem('liveChatLastReadMessageId');
                        setChatSessionId(null);
                        setLiveChatMessages([]);
                        setLastReadMessageId(0);
                        setUnreadCount(0);
                        handleOpenLiveChat();
                      }}
                      className={`px-2 py-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors ${darkMode ? 'text-gray-400' : 'text-gray-600'} text-xs`}
                      title="Start new session"
                    >
                      New
                    </button>
                    <button
                      onClick={handleCloseEmbeddedView}
                      className={`p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}
                    >
                      <X size={20} />
                    </button>
                  </div>
                </div>
              <div className="flex-1 overflow-y-auto p-4 space-y-4">
                {liveChatMessages.map((msg, index) => {
                  const msgDate = msg.timestamp instanceof Date ? msg.timestamp : new Date(msg.timestamp);
                  const prevMsg = index > 0 ? liveChatMessages[index - 1] : null;
                  const prevDate = prevMsg ? (prevMsg.timestamp instanceof Date ? prevMsg.timestamp : new Date(prevMsg.timestamp)) : null;
                  const showDateSep = !prevDate || msgDate.toDateString() !== prevDate.toDateString();
                  return (
                  <React.Fragment key={index}>
                  {showDateSep && (
                    <div className="flex items-center justify-center my-3">
                      <div className={`px-3 py-1 rounded-full text-xs font-medium ${darkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-600'}`}>
                        {getDateLabel(msgDate)}
                      </div>
                    </div>
                  )}
                    <div
                      className={`flex ${msg.type === 'user' ? 'justify-end' : 'justify-start'}`}
                    >
                      <div
                        className={`max-w-[80%] rounded-lg p-3 ${
                          msg.type === 'user'
                            ? 'text-white'
                            : msg.type === 'system'
                            ? darkMode ? 'bg-blue-900/30 text-blue-200' : 'bg-blue-100 text-blue-900'
                            : darkMode
                            ? 'bg-gray-800 text-gray-100'
                            : 'bg-white text-gray-800'
                        }`}
                        style={msg.type === 'user' ? { backgroundColor: theme.primary } : {}}
                      >
                        <p className="text-sm whitespace-pre-line">{msg.text}</p>
                        {msg.attachments && msg.attachments.length > 0 && (
                          <div className="mt-2 space-y-1">
                            {msg.attachments.map((attachment, attIndex) => (
                              <div
                                key={attIndex}
                                className={`flex items-center gap-2 p-2 rounded ${
                                  msg.type === 'user'
                                    ? 'bg-white/20'
                                    : darkMode
                                    ? 'bg-gray-700'
                                    : 'bg-gray-100'
                                }`}
                              >
                                <FileText size={16} />
                                <div className="flex-1 min-w-0">
                                  <p className="text-xs truncate">{attachment.fileName}</p>
                                  <p className="text-xs opacity-70">
                                    {(attachment.fileSize / 1024).toFixed(1)} KB
                                  </p>
                                </div>
                                {!attachment.uploading && attachment.id && (
                                  <button
                                    onClick={() => handleDownloadAttachment(attachment.id, attachment.fileName)}
                                    className={`p-1 rounded hover:bg-white/20 transition-colors`}
                                    title="Download"
                                  >
                                    <Download size={14} />
                                  </button>
                                )}
                                {attachment.uploading && (
                                  <span className="text-xs opacity-70">Uploading...</span>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                        <div className={`flex items-center justify-end gap-2 mt-1`}>
                          <span className={`text-xs opacity-70`}>
                            {formatChatTime(msg.timestamp)}
                          </span>
                          {/* WhatsApp-style message status indicators for user messages */}
                          {msg.type === 'user' && (
                            <span className="ml-1 flex items-center">
                              {msg.status === 'sending' && (
                                <span className="w-3 h-3 border border-white/50 border-t-transparent rounded-full animate-spin" />
                              )}
                              {msg.status === 'sent' && (
                                <Check size={14} className="text-white/70" />
                              )}
                              {msg.status === 'delivered' && (
                                <CheckCheck size={14} className="text-white/70" />
                              )}
                              {msg.status === 'read' && (
                                <CheckCheck size={14} className="text-blue-300" />
                              )}
                              {msg.status === 'failed' && (
                                <span className="text-red-300 text-xs">!</span>
                              )}
                              {/* For messages from server (polled), show as delivered */}
                              {!msg.status && msg.id && !String(msg.id).startsWith('temp_') && (
                                <CheckCheck size={14} className="text-white/70" />
                              )}
                            </span>
                          )}
                          {msg.type === 'user' && msg.status === 'failed' && (
                            <button
                              type="button"
                              onClick={() => handleRetryLiveChatMessage(msg)}
                              className="text-xs text-red-300 hover:text-red-200 underline underline-offset-2"
                            >
                              Retry
                            </button>
                          )}
                          {/* Show "received" indicator for support messages */}
                          {msg.type === 'support' && (
                            <span className={`ml-1 text-xs ${darkMode ? 'text-green-400' : 'text-green-600'}`}>
                              received
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </React.Fragment>
                  );
                })}
                <div ref={messagesEndRef} />
              </div>
              <div className={`p-4 border-t ${darkMode ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-white'}`}>
                {liveChatSuggestions.length > 0 && (
                  <div className="mb-3 flex flex-wrap gap-2">
                    {liveChatSuggestions.map((reply, idx) => (
                      <button
                        key={`${reply.label}-${idx}`}
                        type="button"
                        onClick={() => handleSendLiveChatMessage(reply.value)}
                        className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                          darkMode
                            ? 'bg-gray-700 hover:bg-gray-600 text-gray-200'
                            : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                        }`}
                      >
                        {reply.label}
                      </button>
                    ))}
                  </div>
                )}
                {selectedFile && (
                  <div className={`mb-2 p-2 rounded flex items-center justify-between ${
                    darkMode ? 'bg-gray-700' : 'bg-gray-100'
                  }`}>
                      <div className="flex items-center gap-2">
                        <FileText size={16} />
                        <div>
                          <p className="text-xs font-medium">{selectedFile.name}</p>
                          <p className="text-xs opacity-70">
                            {(selectedFile.size / 1024).toFixed(1)} KB
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={handleRemoveFile}
                        className={`p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors`}
                      >
                        <X size={16} />
                      </button>
                    </div>
                  )}
                  <div className="flex gap-2">
                    <input
                      type="file"
                      ref={fileInputRef}
                      onChange={handleFileSelect}
                      className="hidden"
                      accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt,.csv"
                    />
                    <button
                      onClick={() => fileInputRef.current?.click()}
                      className={`p-2 rounded-lg transition-colors ${
                        darkMode
                          ? 'bg-gray-700 hover:bg-gray-600 text-gray-200'
                          : 'bg-gray-200 hover:bg-gray-300 text-gray-700'
                      }`}
                      aria-label="Attach file"
                      title="Attach file (max 10MB)"
                    >
                      <Paperclip size={20} />
                    </button>
                    <input
                      type="text"
                      value={liveChatInput}
                      onChange={(e) => setLiveChatInput(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && handleSendLiveChatMessage()}
                      placeholder="Type your message..."
                      className={`flex-1 px-4 py-2 rounded-lg border ${
                        darkMode
                          ? 'bg-gray-900 border-gray-700 text-white placeholder-gray-500'
                          : 'bg-gray-50 border-gray-300 text-gray-900 placeholder-gray-400'
                      } focus:outline-none focus:ring-2 focus:ring-opacity-50`}
                      style={{ focusRingColor: theme.primary }}
                    />
                    <button
                      onClick={handleSendLiveChatMessage}
                      className="p-2 rounded-lg text-white transition-colors"
                      style={{ backgroundColor: theme.primary }}
                      aria-label="Send message"
                    >
                      <Send size={20} />
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              // Regular Chat Messages
              <div className="p-4 space-y-4">
                {messages.map((message, index) => (
                  <div
                    key={index}
                    className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`max-w-[80%] rounded-lg p-3 ${
                        message.type === 'user'
                          ? 'text-white'
                          : darkMode
                          ? 'bg-gray-800 text-gray-100'
                          : 'bg-white text-gray-800'
                      }`}
                      style={message.type === 'user' ? { backgroundColor: theme.primary } : {}}
                    >
                      <p className="text-sm whitespace-pre-line">{message.text}</p>
                      <p className={`text-xs mt-1 opacity-70`}>
                        {formatChatTime(message.timestamp)}
                      </p>
                    </div>
                  </div>
                ))}

                {isTyping && (
                  <div className="flex justify-start">
                    <div className={`rounded-lg p-3 ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
                      <div className="flex gap-1">
                        <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                        <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                        <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                      </div>
                    </div>
                  </div>
                )}

                <div ref={messagesEndRef} />
              </div>
            )}
          </div>

          {/* Quick Replies - Hidden per user request */}
          {false && !showCalendly && !showLiveChat && (
            <div className={`p-3 border-t ${darkMode ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-white'}`}>
              <div className="flex flex-wrap gap-2">
                {quickReplies.map((reply, idx) => (
                  <button
                    key={idx}
                    onClick={() => handleQuickReply(reply.value)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                      darkMode
                        ? 'bg-gray-700 hover:bg-gray-600 text-gray-200'
                        : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                    }`}
                  >
                    {reply.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Contact Options - Only show when NOT in Live Chat, Calendly, or Email */}
          {!showLiveChat && !showCalendly && !showEmailForm && (
            <div className={`px-4 py-3 border-t ${darkMode ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-white'}`}>
              <p className={`text-xs font-semibold mb-2 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                Need direct assistance?
              </p>
              <div className="grid grid-cols-2 gap-2 mb-3">
                <button
                  onClick={handleOpenLiveChat}
                  className={`relative flex items-center justify-center gap-1 px-2 py-2 rounded-lg text-xs font-medium transition-colors ${
                    darkMode
                      ? 'bg-purple-600 hover:bg-purple-700 text-white'
                      : 'bg-purple-500 hover:bg-purple-600 text-white'
                  }`}
                >
                  <MessageCircle size={14} />
                  Live Chat
                  {unreadCount > 0 && (
                    <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center animate-pulse">
                      {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                  )}
                </button>
                <button
                  onClick={handleOpenEmailForm}
                  className={`flex items-center justify-center gap-1 px-2 py-2 rounded-lg text-xs font-medium transition-colors ${
                    darkMode
                      ? 'bg-orange-600 hover:bg-orange-700 text-white'
                      : 'bg-orange-500 hover:bg-orange-600 text-white'
                  }`}
                >
                  <Mail size={14} />
                  Send Email
                </button>
                <button
                  onClick={handleOpenCalendly}
                  className={`flex items-center justify-center gap-1 px-2 py-2 rounded-lg text-xs font-medium transition-colors ${
                    darkMode
                      ? 'bg-blue-600 hover:bg-blue-700 text-white'
                      : 'bg-blue-500 hover:bg-blue-600 text-white'
                  }`}
                >
                  <Calendar size={14} />
                  Book a Demo
                </button>
              </div>
              <div className={`space-y-1.5 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                <div className="flex items-center gap-2 text-xs">
                  <Mail size={14} className="flex-shrink-0" />
                  <a href="mailto:info@halisoft.biz" className="hover:underline">info@halisoft.biz</a>
                </div>
              </div>
            </div>
          )}

          {/* AI Chat Input - Only show when NOT in Live Chat, Calendly, or Email */}
          {!showLiveChat && !showCalendly && !showEmailForm && (
            <div className={`p-4 border-t ${darkMode ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-white'}`}>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                  placeholder="Type your message..."
                  className={`flex-1 px-4 py-2 rounded-lg border ${
                    darkMode
                      ? 'bg-gray-900 border-gray-700 text-white placeholder-gray-500'
                      : 'bg-gray-50 border-gray-300 text-gray-900 placeholder-gray-400'
                  } focus:outline-none focus:ring-2 focus:ring-opacity-50`}
                  style={{ focusRingColor: theme.primary }}
                />
                <button
                  onClick={() => handleSendMessage()}
                  className="p-2 rounded-lg text-white transition-colors"
                  style={{ backgroundColor: theme.primary }}
                  aria-label="Send message"
                >
                  <Send size={20} />
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </>
  );

  return ReactDOM.createPortal(chatContent, document.body);
};

export default SupportChatAgent;
